import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    ShoppingCart,
    User,
    Menu,
    X,
    Package,
    Heart,
    ClipboardList,
    LogOut,
    LayoutDashboard,
    UserCircle,
    Star,
    Ticket,
    Settings
} from 'lucide-react';
import logoNew from '../../assets/logo-new.png';
import { useAuthStore } from '../../store/authStore';
import { useCartStore } from '../../store/cartStore';
import './Navbar.scss';

export const Navbar: React.FC = () => {
    const navigate = useNavigate();
    const { user, isAuthenticated, logout } = useAuthStore();
    const cartItemsCount = useCartStore(state => state.getTotalItems());
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);

    const isAdmin = user?.role === 'admin';

    const handleLogout = async () => {
        await logout();
        // Logout sonrası ana sayfaya yönlendir
        window.location.href = '/';
    };

    const navLinks = [
        { path: '/', label: 'Ana Sayfa' },
        { path: '/about', label: 'Hakkımızda' },
        { path: '/products', label: 'Ürünler' },
        { path: '/faq', label: 'SSS' },
        { path: '/contact', label: 'İletişim' },
    ];

    return (
        <nav className="navbar">
            <div className="navbar-container">
                {/* Logo */}
                <Link to="/" className="navbar-logo">
                    <img src={logoNew} alt="Cheesecake İstanbul" />
                </Link>

                {/* Desktop Navigation */}
                <div className="navbar-links" style={{ marginLeft: '2rem', justifyContent: 'center' }}>
                    {navLinks.map(link => (
                        <Link key={link.path} to={link.path} className="nav-link">
                            {link.label}
                        </Link>
                    ))}
                </div>

                {/* Right Actions */}
                <div className="navbar-actions">
                    {isAuthenticated ? (
                        <>
                            {/* Favorites & Cart - Only for customers */}
                            {!isAdmin && (
                                <>
                                    <button 
                                        className="navbar-icon-btn" 
                                        onClick={() => navigate('/favorites')}
                                        title="Favorilerim"
                                    >
                                        <Heart size={20} />
                                    </button>
                                    <button 
                                        className="navbar-icon-btn" 
                                        onClick={() => navigate('/cart')}
                                        title="Sepetim"
                                    >
                                        <ShoppingCart size={20} />
                                        {cartItemsCount > 0 && (
                                            <span className="navbar-badge">{cartItemsCount}</span>
                                        )}
                                    </button>
                                </>
                            )}

                            {/* User Menu */}
                            <div className="user-menu-wrapper">
                                <button
                                    className="user-menu-btn"
                                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                                >
                                    <div className={`user-avatar ${isAdmin ? 'admin' : ''}`}>
                                        {user?.firstName?.[0]}{user?.lastName?.[0]}
                                    </div>
                                    <div className="user-info">
                                        <span className="user-name">
                                            {user?.firstName} {user?.lastName}
                                        </span>
                                        {isAdmin && (
                                            <span className="user-role-badge">Yönetici</span>
                                        )}
                                    </div>
                                </button>

                                {userMenuOpen && (
                                    <div className="user-dropdown">
                                        {isAdmin ? (
                                            <>
                                                <div className="dropdown-header">
                                                    <LayoutDashboard size={16} />
                                                    <span>Yönetim</span>
                                                </div>
                                                <Link to="/admin" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                                                    <LayoutDashboard size={18} />
                                                    Yönetim Paneli
                                                </Link>
                                                <Link to="/admin/orders" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                                                    <ClipboardList size={18} />
                                                    Siparişler
                                                </Link>
                                                <Link to="/admin/products" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                                                    <Package size={18} />
                                                    Ürünler
                                                </Link>
                                                <Link to="/admin/customers" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                                                    <UserCircle size={18} />
                                                    Müşteriler
                                                </Link>
                                            </>
                                        ) : (
                                            <>
                                                <Link to="/profile" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                                                    <UserCircle size={18} />
                                                    Profilim
                                                </Link>
                                                <Link to="/orders" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                                                    <ClipboardList size={18} />
                                                    Tüm Siparişlerim
                                                </Link>
                                                <Link to="/reviews" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                                                    <Star size={18} />
                                                    Değerlendirmelerim
                                                </Link>
                                                <Link to="/coupons" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                                                    <Ticket size={18} />
                                                    İndirim Kuponlarım
                                                </Link>
                                                <Link to="/settings" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                                                    <Settings size={18} />
                                                    Kullanıcı Bilgilerim
                                                </Link>
                                            </>
                                        )}
                                        <div className="dropdown-divider" />
                                        <button className="dropdown-item logout" onClick={handleLogout}>
                                            <LogOut size={18} />
                                            Çıkış Yap
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <button 
                                className="navbar-icon-btn" 
                                onClick={() => navigate('/favorites')}
                                title="Favorilerim"
                            >
                                <Heart size={20} />
                            </button>
                            <button 
                                className="navbar-icon-btn" 
                                onClick={() => navigate('/cart')}
                                title="Sepetim"
                            >
                                <ShoppingCart size={20} />
                            </button>
                            <button className="btn btn-primary" onClick={() => navigate('/login')}>
                                <User size={18} />
                                Giriş Yap
                            </button>
                        </>
                    )}

                    {/* Mobile Menu Toggle */}
                    <button
                        className="mobile-menu-toggle"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    >
                        {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            {mobileMenuOpen && (
                <div className="mobile-menu">
                    {navLinks.map(link => (
                        <Link
                            key={link.path}
                            to={link.path}
                            className="mobile-link"
                            onClick={() => setMobileMenuOpen(false)}
                        >
                            {link.label}
                        </Link>
                    ))}
                    {isAuthenticated && !isAdmin && (
                        <>
                            <div className="mobile-divider" />
                            <Link to="/profile" className="mobile-link" onClick={() => setMobileMenuOpen(false)}>
                                <UserCircle size={18} />
                                Profilim
                            </Link>
                            <Link to="/orders" className="mobile-link" onClick={() => setMobileMenuOpen(false)}>
                                <ClipboardList size={18} />
                                Tüm Siparişlerim
                            </Link>
                            <Link to="/reviews" className="mobile-link" onClick={() => setMobileMenuOpen(false)}>
                                <Star size={18} />
                                Değerlendirmelerim
                            </Link>
                            <Link to="/coupons" className="mobile-link" onClick={() => setMobileMenuOpen(false)}>
                                <Ticket size={18} />
                                İndirim Kuponlarım
                            </Link>
                            <Link to="/favorites" className="mobile-link" onClick={() => setMobileMenuOpen(false)}>
                                <Heart size={18} />
                                Favorilerim
                            </Link>
                            <Link to="/cart" className="mobile-link" onClick={() => setMobileMenuOpen(false)}>
                                <ShoppingCart size={18} />
                                Sepet {cartItemsCount > 0 && `(${cartItemsCount})`}
                            </Link>
                            <Link to="/settings" className="mobile-link" onClick={() => setMobileMenuOpen(false)}>
                                <Settings size={18} />
                                Kullanıcı Bilgilerim
                            </Link>
                        </>
                    )}
                    {isAuthenticated && isAdmin && (
                        <>
                            <div className="mobile-divider" />
                            <Link to="/admin" className="mobile-link" onClick={() => setMobileMenuOpen(false)}>
                                <LayoutDashboard size={18} />
                                Yönetim Paneli
                            </Link>
                        </>
                    )}
                </div>
            )}
        </nav>
    );
};
